import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import CssBaseline from '@mui/material/CssBaseline';
import { GlobalLoading } from './contexts/GlobalLoading';
import { SnackbarProvider } from './contexts/SnackbarProvider';
import ScrollToTop from './utils/ScrollToTop';
import axiosInstance from './utils/axiosInstance';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PickingDetailPage from './pages/PickingDetailPage';
import PickingPage from './pages/PickingPage';
import PickingCheckPage from './pages/PickingCheckPage';
import socket from './utils/socket';
function App() {
  const [isAuth, setIsAuth] = useState(false);
  const [userName, setUserName] = useState('ユーザ');
  const [deviceName, setDeviceName] = useState('未接続');
  const [loading, setLoading] = useState(true);

  const [uuid, setUuid] = useState(null);
  const [warehouseId, setWarehouseId] = useState(null);
  const [deviceSet, setDeviceSet] = useState(false);

  const location = useLocation();

  const isPublicPage = location.pathname === '/login';

  // --- 初期ロードで認証チェック ---
  useEffect(() => {
    const init = async () => {
      try {
        if (!isPublicPage) {
          const userRes = await axiosInstance.get('/login/check-login');
          setUserName(userRes.data.user.username);
          setIsAuth(true);
        }
      } catch {
        setIsAuth(false);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [location.pathname]);

  // --- UUID 初期化 ---
  useEffect(() => {
    const savedUuid = localStorage.getItem('device_uuid');
    if (savedUuid) {
      setUuid(savedUuid);
      setDeviceSet(true);
    }
  }, []);

  // --- UUID / デバイス情報 ---
  useEffect(() => {
    if (!uuid || !isAuth) return;

    const initDevice = async () => {
      try {
        const res = await axiosInstance.post('/picking/me', { device_ip: uuid });
        setWarehouseId(res.data.warehouse_id);
        setDeviceName(res.data.device_name);
      } catch (e) {
        console.error('端末が未設定です', e);
        setDeviceSet(false);
        if (!deviceSet) {
          return <Navigate to="/" replace />;
        }
      }
    };
    initDevice();
  }, [uuid, isAuth]);

  // --- Socket 接続 ---
  useEffect(() => {
    if (!warehouseId) return;
    socket.emit('join-warehouse', warehouseId);
  }, [warehouseId]);

  // --- ロード中 ---
  if (loading) return <GlobalLoading />;

  // --- 未ログインは /login にリダイレクト ---
  if (!isAuth && !isPublicPage) {
    localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ScrollToTop />
      <GlobalLoading>
        <SnackbarProvider>
          {isPublicPage ? (
            <LoginPage setUserName={setUserName} setIsAuth={setIsAuth} />
          ) : (
            <Routes>
              <Route
                element={<Layout userName={userName} deviceName={deviceName} isAuth={isAuth} logout={() => setIsAuth(false)} />}
              >
                <Route
                  path="/"
                  element={
                    <DashboardPage
                      uuid={uuid}
                      deviceSet={deviceSet}
                      setUuid={setUuid}
                      setDeviceSet={setDeviceSet}
                      warehouseId={warehouseId}
                    />
                  }
                />
                <Route
                  path="/picking"
                  element={warehouseId ? <PickingPage warehouseId={warehouseId} /> : <Navigate to="/" replace />}
                />
                <Route
                  path="/picking/:id"
                  element={warehouseId ? <PickingDetailPage warehouseId={warehouseId} uuid={uuid} /> : <Navigate to="/" replace />}
                />
                <Route
                  path="/picking/:id/check/:itemId"
                  element={warehouseId ? <PickingCheckPage warehouseId={warehouseId} /> : <Navigate to="/" replace />}
                />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </SnackbarProvider>
      </GlobalLoading>
    </ThemeProvider>
  );
}
export default App;
